import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { VideoScriptsServices } from "@/services/bedrock";
import { VideoScriptsServices as LegacyVideoScriptsServices } from "@/services";
import type { VideoScriptSearchQuery } from "@/services/bedrock";
import { useCallback, useMemo } from "react";
import { VideoScriptRequest } from "@/interfaces";

const SCRIPT_CACHE_KEY = "coachScriptsList";

function buildVideoScriptsQueryOptions(
  coachId: number | undefined,
  params: VideoScriptSearchQuery = {},
  signal?: AbortSignal
) {
  const sortedCatKeys = params.categoryIds?.slice().sort() || [];
  return {
    queryKey: [
      SCRIPT_CACHE_KEY,
      coachId?.toString(),
      params.searchTerm,
      sortedCatKeys,
      params.sort,
      params.sortDirection,
      params.page,
      params.perPage,
    ] as const,
    queryFn: () => VideoScriptsServices.search(params, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5, // 5 minutes,
  };
}

export const useVideoScriptSearch = (params?: VideoScriptSearchQuery) => {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();

  const queryOptions = useMemo(() => buildVideoScriptsQueryOptions(coachId, params), [coachId, params]);
  const { data: scripts, isFetching } = useQuery(queryOptions);

  const fetchVideoScriptsPage = useCallback(
    (params: VideoScriptSearchQuery, signal?: AbortSignal) =>
      queryClient.fetchQuery(buildVideoScriptsQueryOptions(coachId, params, signal)),
    [coachId, queryClient]
  );

  const totalPages = useMemo(() => scripts?.pagination?.totalPages || 1, [scripts?.pagination?.totalPages]);

  const clearCache = useCallback(async () => {
    return queryClient.invalidateQueries({ queryKey: [SCRIPT_CACHE_KEY] });
  }, [queryClient]);

  return useMemo(
    () => ({
      scripts,
      totalPages,
      isFetching,
      fetchVideoScriptsPage,
      clearCache,
    }),
    [scripts, totalPages, isFetching, fetchVideoScriptsPage, clearCache]
  );
};

export interface SaveVideoScriptRequest {
  id?: number;
  request: VideoScriptRequest;
}

export const useSaveVideoScript = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (info: SaveVideoScriptRequest) => {
      return info.id
        ? LegacyVideoScriptsServices.update(info.id, info.request)
        : LegacyVideoScriptsServices.create(info.request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCRIPT_CACHE_KEY] }).catch(console.error);
    },
  });
};

export const useDeleteVideoScript = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => LegacyVideoScriptsServices.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SCRIPT_CACHE_KEY] }).catch(console.error);
    },
  });
};
